"use client"

import { tourActions } from "@/lib/redux/features/tour/reduce"
import { TypeTourSearchResultItem } from "@/lib/redux/features/tour/types"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { DownOutlined } from "@ant-design/icons"
import { Button, Modal, Tooltip } from "antd"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

const MAX_VISIBLE_ROUTES = 6
const DRAG_CLICK_THRESHOLD_PX = 5

export const extractTourRouteLabel = (tourName: string): string | null => {
  const main = (tourName || "").split("|")[0]?.trim() || ""
  const colonIdx = main.indexOf(":")
  const routePart = colonIdx >= 0 ? main.slice(colonIdx + 1).trim() : main
  const segments = routePart.split(/\s*-\s*/).map((s) => s.trim()).filter(Boolean)
  if (segments.length >= 2) {
    return segments.slice(-2).join(" - ")
  }
  return segments.length === 1 ? segments[0] : null
}

const TourCategoryRouteChips = () => {
  const tours = useAppSelector((state) => state.tour.tours_search.data)
  const routeFilter = useAppSelector((state) => state.tour.formSearch.routeFilter)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [routesModalOpen, setRoutesModalOpen] = useState(false)
  const [canScroll, setCanScroll] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const chipsRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false })

  const routes = useMemo(() => {
    const seen = new Set<string>()
    const labels: string[] = []
    tours.forEach((tour: TypeTourSearchResultItem) => {
      const label = extractTourRouteLabel(tour.tourName)
      if (!label || seen.has(label)) return
      seen.add(label)
      labels.push(label)
    })
    return labels.sort((a, b) => a.localeCompare(b, "vi"))
  }, [tours])

  const visibleRoutes = routes.slice(0, MAX_VISIBLE_ROUTES)
  const hiddenCount = Math.max(0, routes.length - MAX_VISIBLE_ROUTES)

  const updateCanScroll = useCallback(() => {
    const el = chipsRef.current
    if (!el) {
      setCanScroll(false)
      return
    }
    setCanScroll(el.scrollWidth > el.clientWidth + 1)
  }, [])

  useEffect(() => {
    updateCanScroll()
    const el = chipsRef.current
    if (!el) return

    const observer = new ResizeObserver(updateCanScroll)
    observer.observe(el)
    window.addEventListener("resize", updateCanScroll)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", updateCanScroll)
    }
  }, [updateCanScroll, visibleRoutes])

  useEffect(() => {
    const stopDrag = () => {
      dragStateRef.current.active = false
      setIsDragging(false)
    }
    window.addEventListener("mouseup", stopDrag)
    return () => window.removeEventListener("mouseup", stopDrag)
  }, [])

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canScroll || event.button !== 0) return
    const el = chipsRef.current
    if (!el) return

    dragStateRef.current = {
      active: true,
      startX: event.pageX,
      scrollLeft: el.scrollLeft,
      moved: false,
    }
    setIsDragging(true)
  }

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.active) return
    const el = chipsRef.current
    if (!el) return

    const deltaX = event.pageX - dragStateRef.current.startX
    if (Math.abs(deltaX) > DRAG_CLICK_THRESHOLD_PX) {
      dragStateRef.current.moved = true
    }

    el.scrollLeft = dragStateRef.current.scrollLeft - deltaX
  }

  const endDrag = () => {
    dragStateRef.current.active = false
    setIsDragging(false)
  }

  const handleRouteClick = (route: string, closeModal = false) => {
    if (dragStateRef.current.moved) {
      dragStateRef.current.moved = false
      return
    }
    dispatch(tourActions.setTourRouteFilter(routeFilter === route ? undefined : route))
    if (closeModal) {
      setRoutesModalOpen(false)
    }
  }

  const renderRouteChip = (route: string, closeModal = false) => (
    <Tooltip key={route} title={route} overlayStyle={{ maxWidth: 360 }}>
      <span className="tour-category__route-chip-wrap">
        <Button
          type={routeFilter === route ? "primary" : "default"}
          className={`tour-category__route-chip${routeFilter === route ? " tour-category__route-chip--active" : ""}`}
          onClick={() => handleRouteClick(route, closeModal)}
        >
          <span className="tour-category__route-chip-label">{route}</span>
        </Button>
      </span>
    </Tooltip>
  )

  if (!routes.length) return null

  return (
    <>
      <div className="tour-category__routes">
        <div
          ref={chipsRef}
          className={[
            "tour-category__routes-chips",
            canScroll ? "tour-category__routes-chips--draggable" : "",
            isDragging ? "tour-category__routes-chips--dragging" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
        >
          {visibleRoutes.map((route) => renderRouteChip(route))}
        </div>
        {hiddenCount > 0 && (
          <Button
            type="link"
            className="tour-category__routes-more"
            onClick={() => setRoutesModalOpen(true)}
          >
            {t("tour.view_all_routes")} <DownOutlined />
          </Button>
        )}
      </div>
      <Modal
        open={routesModalOpen}
        title={t("tour.all_routes_title", { count: routes.length })}
        footer={null}
        onCancel={() => setRoutesModalOpen(false)}
        className="tour-category__routes-modal"
        width={720}
        destroyOnHidden
      >
        <div className="tour-category__routes-modal-chips">
          {routes.map((route) => renderRouteChip(route, true))}
        </div>
      </Modal>
    </>
  )
}

export default TourCategoryRouteChips
